import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InputLabel } from "@mui/material";

import BaseDrawer from "../../../components/ui/BaseDrawer";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import BaseVirtualizeSelect from "../../../components/ui/BaseVirtualizeSelect";
import {
  setTaskBoardFilter,
  resetTaskBoardFilters,
} from "../../../store/filters/taskBoardFiltersSlice";
import { fetchCompanyUsers } from "../../../services/api/company";
import { useQuery } from "@tanstack/react-query";

const TaskBoardFilters = ({
  open,
  onClose,
  fetchTasks,
  isLoading,
  user,
  isCompanyOwner,
  teamsData,
  teamMembersData,
  allTeamTaskData,
  companyUsersData,
}) => {
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.taskBoardFilters.value);

  const [localFilters, setLocalFilters] = useState({
    title: "",
    taskIds: [],
    teamIds: [],
    createdByIds: [],
    dueDate: { from: null, to: null },
    priority: { value: "", label: "" },
    assignees: [],
    workOrder: { value: "", label: "" },
  });

  const tasksList = allTeamTaskData?.data?.data || [];

  useEffect(() => {
    if (filters) setLocalFilters({ ...localFilters, ...filters });
  }, [filters]);

  const handleApplyFilters = () => {
    Object.entries(localFilters).forEach(([key, val]) => {
      dispatch(setTaskBoardFilter({ filterName: key, data: val }));
    });
    dispatch(setTaskBoardFilter({ filterName: "pagination", data: { page: 0 } }));
    setTimeout(() => {
      fetchTasks?.();
      onClose();
    }, 0);
  };

  const handleResetFilters = () => {
    dispatch(resetTaskBoardFilters());
    setLocalFilters({
      title: "",
      taskIds: [],
      teamIds: [],
      createdByIds: [],
      dueDate: { from: null, to: null },
      priority: { value: "", label: "" },
      assignees: [],
      workOrder: { value: "", label: "" },
    });
  };

  const hasFilters = Object.values(localFilters).some(
    (v) =>
      v &&
      (typeof v === "string"
        ? v.trim() !== ""
        : Array.isArray(v)
          ? v.length > 0
          : v.value || v.label),
  );
  const formattedTaskData = (tasksList || []).map((task) => ({
    value: task.id,
    label: task.title,
  }));

  const formattedTeamMembers = (teamMembersData?.data || []).map((member) => ({
    value: member.id,
    label: `${member.firstName} ${member.lastName}`,
    profilePicture: member.profilePicture,
    teamId: member.teamId,
  }));

  const formattedWorkOrders = [
    ...new Map(
      tasksList
        .filter((task) => task.workOrder)
        .map((task) => [
          task.workOrder.id,
          {
            value: task.workOrder.id,
            label: task.workOrder.referenceId,
          },
        ]),
    ).values(),
  ];

  const formattedTeams = (isCompanyOwner ? teamsData?.data?.data || [] : user.teams || []).map(
    (team) => ({
      value: team.id,
      label: team.name,
    }),
  );

  return (
    <BaseDrawer
      open={open}
      onClose={onClose}
      onApply={handleApplyFilters}
      title={
        <span
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "24px",
            color: "#686868",
            fontWeight: "400",
          }}
        >
          Search Criteria
        </span>
      }
      saveButtonText="Apply Filters"
      isLoading={isLoading}
      enabled={hasFilters}
      handleResetFilters={handleResetFilters}
    >
      <div>
        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Tasks"
            name="taskIds"
            multiple
            placeholder="Select Tasks"
            options={formattedTaskData}
            value={localFilters.taskIds}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                taskIds: e.target.value,
              })
            }
            isClearable
          />
        </div>

        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Priority"
            placeholder="Select Priority"
            options={[
              { value: "HIGH", label: "High" },
              { value: "MEDIUM", label: "Medium" },
              { value: "LOW", label: "Low" },
            ]}
            value={localFilters.priority}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                priority: { value: e.target.value, label: e.target.label },
              })
            }
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            placeholder="Select Created By"
            label="Created By"
            options={[
              { label: `${user?.firstName} ${user?.lastName}`, value: user?.id },
              ...(companyUsersData?.map((user) => ({
                label: `${user.firstName} ${user.lastName}`,
                value: user.id,
              })) || []),
            ]}
            value={localFilters.createdByIds}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                createdByIds: e.target.value,
              })
            }
            multiple
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            placeholder="Select Team"
            label="Team"
            options={formattedTeams}
            value={localFilters.teamIds}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                teamIds: e.target.value,
              })
            }
            multiple
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            placeholder="Select Assignees"
            label="Assignees"
            options={formattedTeamMembers}
            value={localFilters.assignees}
            onChange={(e) => setLocalFilters({ ...localFilters, assignees: e.target.value })}
            multiple
            isClearable
          />
        </div>

        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Work Order"
            placeholder="Select Work Order"
            options={formattedWorkOrders}
            value={localFilters.workOrder}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                workOrder: { value: e.target.value, label: e.target.label },
              })
            }
            isClearable
          />
        </div>
      </div>
    </BaseDrawer>
  );
};

export default TaskBoardFilters;
